import { useAuth } from "../../hooks/useAuth";
import { Button } from "devextreme-react";
import DataGrid, {
    Column,
    Export,
    FilterRow,
    Item,
    KeyboardNavigation,
    Pager,
    Paging,
    SearchPanel,
    Toolbar,
} from 'devextreme-react/data-grid';
import CustomPreparedHeaderCell from "../../components/CustomPreparedHeaderCell";
import { allowedPageSizes, pageFormats } from "../../components/GlobalDataGridConfigurations";
import { ROUTE_WELCOME } from "../../routes/Routes";
import { useNavigate } from "react-router-dom";
import { ROUTE_CREATE_TCM_PROJECTS, ROUTE_TCM_PROJECTS } from "../Routes/Routes";
import { logExport } from "../../hooks/useAuth";
import ExportGrid from "../../components/ExportGrid";
import { useEffect, useState } from "react";

const DataGridTcmProjects = ({ dataSource }) => {
    const { user } = useAuth();
    const [allowEditing, setAllowEditing] = useState(false);
    const [allowCreate, setAllowCreate] = useState(false);
    const navigate = useNavigate();
    const navigateToHomePage = () => navigate(ROUTE_WELCOME.withSlash);
    useEffect(() => {
        const permissions = user.role.ModulePermissions?.filter(module => { return (module.Page === ROUTE_TCM_PROJECTS.module) })
        setAllowEditing(permissions?.filter(module => { return (module.Update) }).length > 0)
        setAllowCreate(permissions?.filter(module => { return (module.Create) }).length > 0)
    }, [])
    const onBackClick = () => {
        navigateToHomePage();
    };

    const handleAdd = () => {
        if (!allowCreate) {
            alert("You are not authorized to create a TCM Project, please contact your administrator for assistance.");
            return;
        }
        navigate(`${ROUTE_CREATE_TCM_PROJECTS.plain}`);
    }
    const onExporting = (e) => {
        logExport(user.email);
        ExportGrid(e, ROUTE_TCM_PROJECTS.title, true, 12);
    };
    function formatZipCode(zip) {
        if (!zip) return zip;

        // Already formatted: 12345-1234
        if (/^\d{5}-\d{4}$/.test(zip)) return zip;

        // 9 digits: 123451234 -> 12345-1234
        if (/^\d{9}$/.test(zip)) {
            return zip.slice(0, 5) + '-' + zip.slice(5);
        }

        // 5 digits: 12345
        if (/^\d{5}$/.test(zip)) return zip;

        // Otherwise, return as is
        return zip;
    }
    return (
        <DataGrid
            columnAutoWidth={true}
            dataSource={dataSource}
            onCellPrepared={CustomPreparedHeaderCell}
            onExporting={e => onExporting(e)}
            showBorders={true}
            showRowLines={true}
            rowAlternationEnabled={true}
        >
            <FilterRow visible={true} />
            <KeyboardNavigation enabled={true} />
            <Paging defaultPageSize={10} />
            <Pager
                showPageSizeSelector={true}
                allowedPageSizes={allowedPageSizes}
            />
            <SearchPanel visible placeholder='Search...' highlightCaseSensitive />
            <Toolbar>
                <Item location='before' locateInMenu='auto'>
                    <Button
                        icon='plus'
                        stylingMode="contained"
                        text="Add TCM Project"
                        onClick={handleAdd}
                        id="add_new_tcm_project_cost_estimate_button_030"
                    />
                </Item>
                <Item name="exportButton" locateInMenu="auto" />
                <Item name="searchPanel" locateInMenu="auto" />
                <Item location='after'>
                    <Button text='Home' icon='home' stylingMode='outlined' type='normal' onClick={onBackClick} />
                </Item>
            </Toolbar>
            <Column
                dataField="Id"
                caption="ID"
                cellRender={(cellData) => (
                    <a
                        href="#"
                        style={{ fontWeight: 'bold', color: '#005288' }}
                        onClick={(e) => {
                            if (!allowEditing) {
                                alert("You are not authorized to edit a TCM Project, please contact your administrator for assistance.");
                                return;
                            }
                            e.preventDefault();
                            navigate(`${cellData.value}`);
                        }}
                    >
                        Edit: {cellData.value}
                    </a>
                )}
                sortOrder="desc"
                fixed={true}
                fixedPosition="left"
            />
            <Column
                dataField="ProjectDescription"
                caption="Project Description"
            />
            <Column
                dataField="ProjectStartDate"
                caption="Project Start Date"
            />
            <Column
                dataField="ProjectEndDate"
                caption="Project End Date"
            />
            <Column
                dataField="Building.RegionNumber"
                caption="Region"
            // fixed={true}
            />
            <Column
                dataField="Building.SequenceNumber"
                caption="Building No."
            // fixed={true}
            />
            <Column
                dataField="Building.Name"
                caption="Building Name"
            />
            <Column
                caption="Address"
                dataField="Building.Location"
                calculateCellValue={(data) => {
                    const location = data.Building?.Location || {};

                    // Utility to check for valid address part
                    const isValid = (val) =>
                        val !== null &&
                        val !== undefined &&
                        val !== 'null' &&
                        !(typeof val === 'number' && isNaN(val)) &&
                        val !== '';

                    const addressParts = [];
                    if (isValid(location.Address1)) addressParts.push(location.Address1);
                    if (isValid(location.Address2)) addressParts.push(location.Address2);

                    // Only add city/state/zip if at least one is valid
                    const cityStateZipArr = [
                        isValid(location.CityName) ? location.CityName : '',
                        isValid(location.State) ? location.State : '',
                        isValid(formatZipCode(location.ZipCode)) ? formatZipCode(location.ZipCode) : ''
                    ].filter(isValid);

                    if (cityStateZipArr.length) addressParts.push(cityStateZipArr.join(' '));

                    // Filter out any empty strings before joining
                    const filteredParts = addressParts.filter(isValid);

                    return filteredParts.length > 0 ? filteredParts.join(', ') : '';
                }}
            />

            <Column
                caption="Agency(s)"
                width={150}
                calculateCellValue={data => {
                    const customers = data.Customers || [];
                    return customers.length === 0
                        ? 'No Agencies'
                        : customers.map(c => c.AgencyName).join(', ');
                }}
                cellRender={
                    (cellData) => {
                        const customers = cellData.data.Customers || [];
                        let agencyNames = customers.length === 0
                            ? 'No Agencies'
                            : customers.map(c => c.AgencyName).join(', ');

                        // Truncate to ~175 characters
                        const maxLength = 25;
                        const truncated = agencyNames.length > maxLength
                            ? agencyNames.slice(0, maxLength) + '...'
                            : agencyNames;

                        return (
                            <div
                                style={{
                                    whiteSpace: 'normal',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    cursor: 'pointer'
                                }}
                                title={agencyNames} // Native HTML tooltip
                            >
                                {truncated}
                            </div>
                        );
                    }
                }
            />
            <Column
                caption="Project Equipment Total"
                dataField="ProjectTotal"
                calculateCellValue={data => {
                    const details = data.TCMCostEstimates || [];
                    const total = details.reduce((sum, detail) => sum + (detail.EquipmentTotal || 0), 0);
                    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(total);
                }}
            />
            <Column
                caption="Project Labor Total"
                // dataField="ProjectLaborTotal"
                calculateCellValue={data => {
                    const details = data.TCMCostEstimates || [];
                    // console.log(data);
                    const total = details.reduce((sum, detail) => sum + (detail.LaborTotal || 0), 0);
                    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(total);
                }}
            />

            <Column
                caption="Grand Total"
                dataField="GrandTotal"
                calculateCellValue={data => {
                    const details = data.TCMCostEstimates || [];
                    const total = details.reduce((sum, detail) => sum + (detail.GrandTotal || 0), 0);
                    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(total);
                }}
            />
            <Column
                dataField="Comments"
                caption="Comments"
            />
            <Export
                enabled={true}
                formats={pageFormats}
            />
        </DataGrid>
    )
}
export default DataGridTcmProjects;
